import { BaseGameRecorder, generateId } from '@bn-games/history-core';
import type { GameStateData } from '../game/GameState.js';
import { ActiveGameStore, toActiveSession } from './ActiveGameStore.js';
import type { ActiveSession, GameRecord } from './types.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';

/**
 * Owns the in-flight `ActiveSession` for the current sudoku puzzle. The base
 * class handles session id + storage write-through; this subclass snapshots the
 * live `GameStateData` on every mutation via `record(state)` and emits a
 * `GameRecord` when the puzzle is finished.
 *
 * There is no per-move replay (re-attempt is a fresh solve, not playback), so
 * we don't track an append-only move journal like checkers/domino — every save
 * is a full serialized snapshot of the engine state.
 */
export class GameRecorder extends BaseGameRecorder<ActiveSession> {
  constructor() {
    super(ActiveGameStore);
  }

  /** Begin recording a fresh puzzle. */
  start(state: GameStateData, now: number = Date.now()): void {
    this.setSession(toActiveSession(state, generateId(), now));
  }

  /** Adopt an existing session restored from localStorage (resume path). */
  resume(session: ActiveSession): void {
    this.adopt(session);
  }

  /** Snapshot the current engine state. Call after every mutating engine op. */
  record(state: GameStateData, now: number = Date.now()): void {
    this.updateSession((s) => toActiveSession(state, s.id, now));
  }

  /** Produce the finished-puzzle record and clear the active slot. Pass
   *  `forfeited: true` when the player gave up and revealed the solution. */
  finish(
    state: GameStateData,
    opts: { readonly forfeited?: boolean } = {},
    now: number = Date.now(),
  ): GameRecord {
    if (!this.session) {
      throw new Error('GameRecorder.finish called with no active session');
    }
    const record: GameRecord = {
      id: this.session.id,
      version: HISTORY_SCHEMA_VERSION,
      seed: state.puzzle.seed,
      difficulty: state.puzzle.difficulty,
      generatorVersion: state.puzzle.generatorVersion,
      timeMs: Math.max(0, now - state.startedAt),
      hintsUsed: state.hintsUsed,
      errorsMade: state.errorsMade,
      finishedAt: now,
      ...(opts.forfeited === true ? { forfeited: true } : {}),
    };
    this.discard();
    return record;
  }
}
